import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useVideoPlatformStore } from "../../store/videoPlatformStore";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChatIcon from "@mui/icons-material/Chat";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FilterableDataGrid, FilterConfig } from "../common/FilterableDataGrid";
import { MeetingAttendeesDialog } from "./MeetingAttendeesDialog";
import { MeetingChatDialog } from "./MeetingChatDialog";
import { getStatusColor, formatDateTime } from "./utils";
import { useSnackbar } from "../../hooks/useSnackbar";

interface VideoPlatformListProps {
  onView: (meetingId: string) => void;
  onDelete: (meetingId: string) => void;
}

type RowAction = {
  key: string;
  label: string;
  icon: React.ReactNode;
  color: React.ComponentProps<typeof IconButton>["color"];
  onClick: () => void;
};

// Renders row actions as a single overflow menu (used on small screens to
// avoid the wide multi-icon column overflowing the viewport).
const RowActionsMenu: React.FC<{ actions: RowAction[] }> = ({ actions }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              action.onClick();
              setAnchorEl(null);
            }}
          >
            <ListItemIcon sx={{ color: `${action.color}.main` }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const VideoPlatformList: React.FC<VideoPlatformListProps> = ({
  onView,
  onDelete,
}) => {
  const { meetings, loadMeetings } = useVideoPlatformStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [attendeesDialogOpen, setAttendeesDialogOpen] = React.useState(false);
  const [selectedMeetingForAttendees, setSelectedMeetingForAttendees] =
    React.useState<{
      id: string;
      title: string;
    } | null>(null);
  const [chatDialogOpen, setChatDialogOpen] = React.useState(false);
  const [selectedMeetingForChat, setSelectedMeetingForChat] = React.useState<{
    id: string;
    title: string;
  } | null>(null);
  const { showSnackbar, SnackbarNode } = useSnackbar();

  const handleViewAttendees = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      setSelectedMeetingForAttendees({
        id: meeting.externalMeetingId || meeting.id,
        title: meeting.title,
      });
      setAttendeesDialogOpen(true);
    }
  };

  const handleCloseAttendeesDialog = () => {
    setAttendeesDialogOpen(false);
    setSelectedMeetingForAttendees(null);
  };

  const handleViewChat = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      setSelectedMeetingForChat({
        id: meeting.externalMeetingId || meeting.id,
        title: meeting.title,
      });
      setChatDialogOpen(true);
    }
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedMeetingForChat(null);
  };

  // Status is filtered server-side so Ended/Cancelled meetings (windowed out of
  // the default upcoming view) are actually fetched. Type stays client-side.
  const handleFilterChange = (field: string, value: string) => {
    if (field === "status") {
      loadMeetings(value);
    }
  };

  const handleCopyMeeting = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      navigator.clipboard.writeText(JSON.stringify(meeting, null, 2));
      showSnackbar("Meeting JSON copied to clipboard!");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: isMobile ? 140 : 200,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const colors = getStatusColor(params.value as string);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                backgroundColor: colors.bgcolor,
                color: colors.color,
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "0.8125rem",
                fontWeight: 500,
                lineHeight: 1.5,
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      renderCell: (params) => params.value || "",
    },
    {
      field: "startDate",
      headerName: "Scheduled Start",
      width: isMobile ? 150 : 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    ...(isMobile
      ? []
      : [
          {
            field: "endDate",
            headerName: "Scheduled End",
            width: 180,
            renderCell: (params) => formatDateTime(params.value),
          } as GridColDef,
        ]),
    {
      field: "actions",
      headerName: "Actions",
      width: isMobile ? 80 : 250,
      sortable: false,
      renderCell: (params) => {
        const isEnded = params.row.status?.toUpperCase() === "ENDED";
        const actions: RowAction[] = [
          {
            key: "view",
            label: "View Details",
            icon: <VisibilityIcon fontSize="small" />,
            color: "primary",
            onClick: () => onView(params.row.id),
          },
          {
            key: "attendees",
            label: "View Attendees",
            icon: <PeopleIcon fontSize="small" />,
            color: "info",
            onClick: () => handleViewAttendees(params.row.id),
          },
          {
            key: "chat",
            label: "View Chat",
            icon: <ChatIcon fontSize="small" />,
            color: "secondary",
            onClick: () => handleViewChat(params.row.id),
          },
          {
            key: "copy",
            label: "Copy JSON",
            icon: <ContentCopyIcon fontSize="small" />,
            color: "default",
            onClick: () => handleCopyMeeting(params.row.id),
          },
          ...(!isEnded
            ? [
                {
                  key: "end",
                  label: "End Meeting",
                  icon: <StopCircleIcon fontSize="small" />,
                  color: "error" as const,
                  onClick: () => onDelete(params.row.id),
                },
              ]
            : []),
        ];

        if (isMobile) {
          return <RowActionsMenu actions={actions} />;
        }

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {actions.map((action) => (
              <Tooltip key={action.key} title={action.label}>
                <IconButton
                  size="small"
                  onClick={action.onClick}
                  color={action.color}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
  ];

  // Category options come from the loaded meetings (distinct, non-empty).
  // Values are upper-cased to match FilterableDataGrid's case-insensitive compare.
  const categoryOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    meetings.forEach((m) => {
      const category = m.category?.trim();
      if (category) seen.set(category.toUpperCase(), category);
    });
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [meetings]);

  const filters: FilterConfig[] = [
    {
      label: "Status",
      field: "status",
      options: [
        { value: "ALL", label: "All Statuses" },
        { value: "ACTIVE", label: "Active" },
        { value: "ENDED", label: "Ended" },
        { value: "CREATED", label: "Created" },
        { value: "CANCELLED", label: "Cancelled" },
      ],
    },
    {
      label: "Category",
      field: "category",
      options: [{ value: "ALL", label: "All Categories" }, ...categoryOptions],
    },
  ];

  return (
    <>
      <FilterableDataGrid
        rows={meetings}
        columns={columns}
        filters={filters}
        searchFields={["title", "id", "externalMeetingId"]}
        searchPlaceholder="Search by title or ID"
        height={600}
        pageSize={10}
        onFilterChange={handleFilterChange}
      />

      <MeetingAttendeesDialog
        open={attendeesDialogOpen}
        meetingId={selectedMeetingForAttendees?.id || null}
        meetingTitle={selectedMeetingForAttendees?.title || ""}
        onClose={handleCloseAttendeesDialog}
      />

      <MeetingChatDialog
        open={chatDialogOpen}
        meetingId={selectedMeetingForChat?.id || null}
        meetingTitle={selectedMeetingForChat?.title || ""}
        onClose={handleCloseChatDialog}
      />

      {SnackbarNode}
    </>
  );
};
